import { atom, injectSelf, injectAtomValue, injectAtomInstance, injectEffect, injectCallback, api, ion, injectWhy, getEcosystem } from '@zedux/react';
import { ITopic, IMessage, IContext, IMessagePost, IMessageSeen, IFetchMessagesRequest, IFetchMessagesResponse, INotifySeenMessage, INewTopicResponse, IFilter, IReceivedMessage, FilterTypes, TopicTypes, IUploadProgress, MessageTypes } from '../types'
import { socketAtom, userInfoAtom } from './socket'
import { activeTopicAtom, topicSelector, loadingTopicsAtom } from './atoms'
import { messageManagerAtom } from './messageManagerAtom';
import { NO_FILTER, selectedFilterAtom } from './filterAtoms';
import { topicsAtom } from './topicsAtom';
import { findMessageTypeFromMime } from '../functions/mime';
import { message_with_sender_info } from '../functions/messageUtils';
import { messagesAtomFamily } from './messagesAtomFamily';

const FILE_UPLOAD_CHUNK_SIZE = 128 * 1024;

const getFileHash = async (file: Blob) => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    return Array.from(new Uint8Array(hashBuffer)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const createUploadProgressMessage = (message: IMessage, progress: IUploadProgress, status: IMessage['upload_status']) => ({
    ...message,
    status: 'pending' as const,
    upload_status: status,
    upload_progress: progress,
});

export const messageReceiverAtom = atom("message_receiver", (context: IContext) => {
    const { ecosystem } = injectSelf();
    const socket = injectAtomValue(socketAtom);
    const activeTopicIDInstance = injectAtomInstance(activeTopicAtom, [context]);
    const { updateTopic, addOrUpdateTopic, removeTopic, updateTopicLastMessage } = injectAtomInstance(topicsAtom, [context]).exports;

    const topicInstancesInstance = injectAtomInstance(messageManagerAtom, [context, NO_FILTER]);
    const socketInstance = injectAtomInstance(socketAtom);
    const userInfoInstance = injectAtomInstance(userInfoAtom);
    const loadingTopicsInstance = injectAtomInstance(loadingTopicsAtom);
    // const topicsInstancesStore = injectStore(topicInstances)
    // injectEffect(() => {
    //     topicsInstancesStore.setState(topicInstances)
    // }, [topicInstances])
    injectEffect(() => {
        if (socket) {
            socket.on('message_sent', (message: IMessage, topic_id: number, temporary_message_id: number, new_topic?: ITopic) => {
                console.log("message sent received in messageReceiverAtom ", message);

                if (new_topic) {
                    // First message to a new contact: a real topic was just created on the server
                    addOrUpdateTopic(new_topic); // adds real topic, clears temp topic

                    // Switch the active topic to the newly created real topic if the sender is still viewing the temp topic
                    if (activeTopicIDInstance.getState() === topic_id) {
                        activeTopicIDInstance.setState(new_topic.topic_id)
                    }

                    // Remove the pending message from the temporary topic's messages store
                    const oldInstance = topicInstancesInstance.getState()?.[topic_id]
                    if (oldInstance) {
                        oldInstance.exports.deleteMessage(temporary_message_id)
                    }

                    // Add the confirmed message to the new real topic's messages store (if the instance exists yet)
                    const newInstance = topicInstancesInstance.getState()?.[new_topic.topic_id]
                    const userInfo = userInfoInstance.getState();
                    if (newInstance && userInfo) {
                        newInstance.exports.addMessage(message_with_sender_info(message, userInfo, new_topic))
                    }
                    return
                }

                const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined;
                if (temporary_message_id && topic) {
                    const instance = topicInstancesInstance.getState()?.[topic_id]
                    const userInfo = userInfoInstance.getState()
                    if (instance && userInfo) {
                        instance.exports.setMessageAsSent(temporary_message_id, message_with_sender_info(message, userInfo, topic))
                    }
                    if (!new_topic) {
                        updateTopicLastMessage(topic_id, {
                            last_message_text: message.text,
                            last_message_type: message.message_type,
                            last_message_created_at: message.created_at,
                            last_message_deleted: false,
                        })
                    }
                }
            })

            socket.on('message_not_sent', (message) => {
                console.log("message not sent received in messageReceiverAtom ", message);
                const { temporary_message_id, topic_id, error } = message;
                if (temporary_message_id && topic_id) {
                    const instance = topicInstancesInstance.getState()?.[topic_id];
                    if (instance) {
                        instance.exports.setMessageAsNotSent(temporary_message_id);
                    }
                }
            });

            socket.on('message_received', (message: IReceivedMessage, new_topic_response?: INewTopicResponse) => {
                console.log("message received in messageReceiverAtom ", message);

                // verify if we are in sender or receiver side, because the server emits "message_received" for both sender and receiver after sending a message
                const userInfo = userInfoInstance.getState();
                if (!userInfo) {
                    return
                }

                let new_topic_info: ITopic | null = null;

                if (new_topic_response) {
                    const { topic_sender, topic_receiver, receiver_id, sender_id } = new_topic_response
                    const weAreSender = (sender_id === userInfo.userId || (userInfo.bossId != null && sender_id === userInfo.bossId))
                    const weAreReceiver = (receiver_id === userInfo.userId || (userInfo.bossId != null && receiver_id === userInfo.bossId))

                    if (weAreSender) {
                        new_topic_info = { ...topic_sender } as ITopic;
                    } else if (weAreReceiver) {
                        new_topic_info = { ...topic_receiver } as ITopic;
                    }
                }
                if (new_topic_info) {
                    // A new topic was created (other side sent their first message to us)
                    addOrUpdateTopic(new_topic_info); // adds real topic to list, clears any matching temp topic
                }

                const instance = topicInstancesInstance.getState()?.[message.topic_id];
                if (instance) {
                    // strip topic_id from message
                    const { topic_id, ...newMessage } = message;
                    const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined;
                    const userInfo = userInfoInstance.getState();
                    if (!userInfo) {
                        return
                    }
                    if (topic) {
                        instance.exports.addMessage(message_with_sender_info(newMessage, userInfo, topic));
                    }
                    if (topic_id === activeTopicIDInstance.getState()) {
                        // When Topic is Active
                        notifyServerForMessageSeen(topic_id);
                    } else if (!new_topic_info && topic) {
                        // When topic is not active, we increase unseen count
                        const { viewer_identity_id } = message;
                        if (viewer_identity_id !== userInfo.userId &&
                            (userInfo.bossId == null ||
                                viewer_identity_id !== userInfo.bossId ||
                                topic.other_user_id == userInfo.bossId ||
                                (topic.topic_type == TopicTypes.GROUP || topic.topic_type == TopicTypes.CHANNEL))
                        ) {
                            updateTopic(topic_id, { unseen_count: parseInt(topic?.unseen_count as any || 0) + 1 });
                        }
                    }
                    updateTopic(topic_id, {
                        last_message_text: message.text,
                        last_message_type: message.message_type,
                        last_message_created_at: message.created_at,
                        last_message_deleted: false,
                    });
                }
            });

            // Somebody (other participant or ourself) has seen all messages in a topic, 
            socket.on('message_seen', ({ topic_id, message_id, viewer_identity_id }: IMessageSeen) => {
                const userInfo = userInfoInstance.getState()
                const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined
                if (!userInfo || !topic) {
                    return
                }
                console.log("message seen received in messageReceiverAtom ", topic_id, message_id, viewer_identity_id, userInfo)
                if (viewer_identity_id == userInfo.userId ||
                    (userInfo.bossId != null && viewer_identity_id == userInfo.bossId && topic.other_user_id != userInfo.bossId &&
                        (topic.topic_type != TopicTypes.GROUP && topic.topic_type != TopicTypes.CHANNEL)
                    )) {
                    // Seen is from our side
                    updateTopic(topic_id, { unseen_count: 0 });
                } else {
                    const instance = topicInstancesInstance.getState()?.[topic_id]
                    if (instance) {
                        instance.exports.setMessagesAsSeen(message_id)
                    }
                }
            });

            socket.on('message_edited', (message) => {
                console.log("message edited in messageReceiverAtom ", message);
                const { topic_id, ...newMessage } = message;
                const instance = topicInstancesInstance.getState()?.[topic_id];
                const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined;
                const userInfo = userInfoInstance.getState();
                if (instance && topic && userInfo) {
                    const withSenderInfo = message_with_sender_info(newMessage, userInfo, topic);
                    instance.exports.updateMessage({ ...withSenderInfo, status: '', edit_text: undefined }, false);
                    updateTopicLastMessage(topic_id, { 
                        last_message_text: withSenderInfo.text,
                        last_message_type: withSenderInfo.message_type,
                        last_message_created_at: withSenderInfo.created_at,
                        last_message_deleted: false,
                    });
                }
            });

            socket.on('message_deleted', ({ message_id, topic_id }) => {
                console.log("message deleted in messageReceiverAtom ", message_id, topic_id);
                const instance = topicInstancesInstance.getState()?.[topic_id];
                if (instance) {
                    instance.exports.deleteMessage(message_id);

                    if (topic_id !== activeTopicIDInstance.getState()) {
                        const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined;
                        if (topic) {
                            const currentUnseen = parseInt(topic.unseen_count as any || 0);
                            if (currentUnseen > 0) {
                                updateTopic(topic_id, { unseen_count: currentUnseen - 1 });
                            }
                        }
                    }
                }
            });

            socket.on('new_topic_group', (topic: ITopic) => {
                console.log("new_topic_group received in messageReceiverAtom ", topic);
                addOrUpdateTopic(topic);
            });

            socket.on('update_topic_group', (topic: ITopic) => {
                console.log("updated topic received in messageReceiverAtom ", topic);
                addOrUpdateTopic(topic);
            });

            socket.on('remove_topic', (topic_id: number) => {
                console.log("remove_topic received in messageReceiverAtom ", topic_id);
                removeTopic(topic_id);
            });

            return () => {
                socket?.off('message_received');
                socket?.off('message_sent');
                socket?.off('message_not_sent');
                socket?.off('message_seen');
                socket?.off('message_edited');
                socket?.off('message_deleted');
                socket?.off('new_topic_group');
                socket?.off('update_topic_group');
                socket?.off('remove_topic');
            };
        }
    }, [socket, activeTopicIDInstance]);

    const sendMessage = injectCallback(async (message: IMessagePost | Blob) => {
        if (message instanceof Blob) {
            return
        }

        const messagePost = message as IMessagePost
        const topic_id = messagePost.topic_id;
        const instance = topicInstancesInstance.getState()?.[topic_id];
        const socket = socketInstance.getState()
        const userInfo = userInfoInstance.getState()
        console.log('sending message from messageReceiverAtom ', message, socket, userInfo, instance)
        if (instance && socket && userInfo) {
            const temporaryId = -Date.now(); // temporary ID, will be replaced by real ID from server
            const pendingMessagePost = { ...messagePost, temporary_message_id: temporaryId } as IMessagePost;
            const newPendingMessage = {
                message_id: temporaryId,
                sender_id: userInfo.userId,
                viewer_identity_id: userInfo?.userId ?? 0,
                status: "pending",
                created_at: Date.now(),
                edited_at: undefined,
                sender_is_me: true,
                sender_is_sys: false,
                sender_username: userInfo?.username ?? '',
                is_seen: false,
                filename: messagePost.filename,
                file_content: messagePost.file_content,
                text: messagePost.text,
                mime_type: messagePost.mime_type ?? '',
                reply_id: messagePost.reply_id,
                forward_id: messagePost.forward_id,
                messagePost: pendingMessagePost,
                object_type: messagePost.object_type ?? null,
                object_id: messagePost.object_id ?? null,
                message_type: messagePost.object_type && messagePost.object_id
                    ? MessageTypes.OBJECT
                    : findMessageTypeFromMime(messagePost.mime_type),
                ...(messagePost.file_content instanceof Blob ? {
                    upload_status: 'uploading' as const,
                    upload_progress: {
                        total_chunks: 0,
                        uploaded_chunks: 0,
                        chunk_size: FILE_UPLOAD_CHUNK_SIZE,
                    } as IUploadProgress,
                } : {}),
            } as IMessage;

            instance.exports.addMessage(newPendingMessage);

            try {
                if (message.file_content instanceof Blob) {
                    const file = message.file_content;
                    const fileBuffer = await file.arrayBuffer();
                    const totalChunks = Math.ceil(fileBuffer.byteLength / FILE_UPLOAD_CHUNK_SIZE);
                    const fileHash = await getFileHash(file);

                    const started = await socket.timeout(600000).emitWithAck('start_file_upload', {
                        temporary_message_id: temporaryId,
                        topic_id,
                        context: messagePost.context,
                        text: messagePost.text,
                        filename: messagePost.filename,
                        mime_type: messagePost.mime_type,
                        reply_id: messagePost.reply_id ?? null,
                        forward_id: messagePost.forward_id ?? null,
                        created_at: Date.now(),
                        file_size: file.size,
                        file_hash: fileHash,
                        total_chunks: totalChunks,
                        chunk_size: FILE_UPLOAD_CHUNK_SIZE,
                    });

                    if (!started?.success) {
                        throw new Error(started?.error || 'Upload initialization failed');
                    }

                    for (let index = 0; index < totalChunks; index++) {
                        const start = index * FILE_UPLOAD_CHUNK_SIZE;
                        const end = Math.min(start + FILE_UPLOAD_CHUNK_SIZE, fileBuffer.byteLength);
                        const chunk = fileBuffer.slice(start, end);
                        const result = await socket.timeout(600000).emitWithAck('file_chunk', {
                            temporary_message_id: temporaryId,
                            topic_id,
                            context: message.context,
                            chunk_index: index,
                            total_chunks: totalChunks,
                            chunk_data: chunk,
                        });

                        if (!result?.success) {
                            throw new Error(result?.error || 'Chunk upload failed');
                        }

                        const progress: IUploadProgress = {
                            total_chunks: totalChunks,
                            uploaded_chunks: index + 1,
                            chunk_size: FILE_UPLOAD_CHUNK_SIZE,
                        };
                        instance.exports.updateMessage(createUploadProgressMessage({ ...newPendingMessage, message_id: temporaryId }, progress, 'uploading') as IMessage, false);
                    }

                    instance.exports.updateMessage(createUploadProgressMessage({ ...newPendingMessage, message_id: temporaryId }, {
                        total_chunks: totalChunks,
                        uploaded_chunks: totalChunks,
                        chunk_size: FILE_UPLOAD_CHUNK_SIZE,
                    }, 'done') as IMessage, false);
                    return;
                }

                socket.emit("send_message", pendingMessagePost);
            } catch (error) {
                console.error('Error sending message', error);
                instance.exports.updateMessage({
                    ...newPendingMessage,
                    message_id: temporaryId,
                    status: 'error',
                    upload_status: 'error',
                    upload_progress: {
                        total_chunks: 0,
                        uploaded_chunks: 0,
                        chunk_size: FILE_UPLOAD_CHUNK_SIZE,
                    } as IUploadProgress,
                    upload_error: error instanceof Error ? error.message : 'Upload failed',
                } as IMessage, false);
                socket.emit('message_not_sent', {
                    temporary_message_id: temporaryId,
                    topic_id,
                    error: error instanceof Error ? error.message : 'Upload failed',
                });
            }
        }
    }, [topicInstancesInstance, socketInstance]);

    const resendMessage = injectCallback((topic_id: number, message_id: number) => {
        const instance = topicInstancesInstance.getState()?.[topic_id];
        const socket = socketInstance.getState()
        const userInfo = userInfoInstance.getState();
        if (instance && socket && userInfo) {
            const message = instance.getState().find(m => m.message_id === message_id);
            if (message && message.messagePost) {
                instance.exports.updateMessage({ ...message, status: "pending" } as IMessage, false);
                socket.emit("send_message", message.messagePost);
            }
        }
    });

    const fetchMessages = injectCallback(async (topic_id: number, args?: { upto?: number, filter?: IFilter }) => {
        // const instance = topicInstancesInstance.getState()?.[topic_id]
        const filter = args?.filter ?? NO_FILTER;
        const instance = ecosystem.getInstance(messagesAtomFamily, [topic_id, filter, context]);

        const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined;

        if (instance) {
            const { getMinimumAndMaximum, getAllIsLoaded, setAsAllLoaded } = instance.exports;
            const [leastId, maxId] = getMinimumAndMaximum();
            let upto = args?.upto;
            if (upto != null && leastId != null && upto >= leastId) {
                return // Don't fetch if the upto ID is greater than the least ID we have, because it means we already have those messages
            }
            const socket = socketInstance.getState()
            const userInfo = userInfoInstance.getState();
            const loadingTopics = loadingTopicsInstance.getState();
            const setLoadingTopics = loadingTopicsInstance.setState;

            if (socket && userInfo && topic && topic_id && !loadingTopics.includes(topic_id) && !getAllIsLoaded()) {
                setLoadingTopics([...loadingTopics, topic_id]);
                try {

                    const res = await socket.timeout(10000)
                        .emitWithAck('fetch_messages', { topic_id, before: leastId, upto, filter: args?.filter, context } as IFetchMessagesRequest) as IFetchMessagesResponse;

                    const { success, messages } = res || {};
                    const resolvedTopic = res?.topic ?? res?.new_topic;
                    // console.log('fetch result', success, messages, Array.isArray(messages), resolvedTopic);

                    if (success != true) {
                        console.error("Error fetching messages: ", res.error, topic, topic_id);
                    } else if (Array.isArray(messages)) {
                        const destinationTopic = resolvedTopic ?? topic;
                        if (resolvedTopic) {
                            addOrUpdateTopic(resolvedTopic);
                        }

                        if (messages.length === 0) {
                            setAsAllLoaded();
                        } else if (destinationTopic) {
                            const messagesWithSender = messages.map(m => message_with_sender_info(m, userInfo, destinationTopic));
                            const destinationInstance = destinationTopic.topic_id === topic_id
                                ? instance
                                : ecosystem.getInstance(messagesAtomFamily, [destinationTopic.topic_id, filter, context]);
                            destinationInstance.exports.addMessages(messagesWithSender);
                        }
                    } else {
                        console.error("Error fetching messages: ", messages);
                    }
                } catch (e) {
                    console.error("Error fetching messages: ", e);
                }
                setLoadingTopics(loadingTopics.filter(id => id !== topic_id));
            }
        }
    }, [topicInstancesInstance, context]);

    const notifyServerForMessageSeen = injectCallback((topic_id: number) => {
        const socket = socketInstance.getState()
        if (socket) {
            // Send that we seen the topic messages
            socket.emit("message_seen", { topic_id: topic_id } as INotifySeenMessage);
        }
    }, []);

    const editMessage = injectCallback(async (message_id: number, topic_id: number, new_text: string) => {
        const socket = socketInstance.getState()
        const instance = topicInstancesInstance.getState()?.[topic_id];
        if (socket && instance) {
            instance.exports.updateMessage({ message_id, edit_text: new_text, status: "editing" } as IMessage, false);
            const resetMessage = () => {
                instance.exports.updateMessage({ message_id, status: '', edit_text: undefined } as IMessage, false);
            }
            try {
                const res = await socket.timeout(10000).emitWithAck('edit_message', { message_id, topic_id, new_text });
                const { message, success } = res || {};

                const instance = topicInstancesInstance.getState()?.[topic_id];

                const topic = ecosystem.select(topicSelector, context, topic_id) as ITopic | undefined;
                const userInfo = userInfoInstance.getState();
                if (!instance || !topic || !userInfo) return;

                if (success && message) {
                    const withSenderInfo = message_with_sender_info(message, userInfo, topic);
                    instance.exports.updateMessage({ ...withSenderInfo, status: '', edit_text: undefined }, false);
                    updateTopicLastMessage(topic_id, { 
                        last_message_text: withSenderInfo.text,
                        last_message_type: withSenderInfo.message_type,
                        last_message_created_at: withSenderInfo.created_at,
                        last_message_deleted: false,
                    });
                    return
                }
            } catch (e) {
                console.error("Error editing message: ", e);
            }
            resetMessage();

        }
    }, []);

    const deleteMessage = injectCallback(async (message_id: number, topic_id: number) => {
        const socket = socketInstance.getState()
        const instance = topicInstancesInstance.getState()?.[topic_id];
        if (socket && instance) {
            try {
                const res = await socket.timeout(10000).emitWithAck('delete_message', { message_id, topic_id });
                const { success } = res || {};
                if (success) {
                    instance.exports.deleteMessage(message_id);
                }
            } catch (e) {
                console.error("Error deleting message: ", e);
            }
        }
    }, []);

    const create_group = injectCallback(async (group_name: string, is_channel: boolean, user_ids: number[], image_id: number | null) => {
        const socket = socketInstance.getState();
        if (socket) {
            try {
                const res = await socket.timeout(10000).emitWithAck('create_group', { group_name, is_channel, user_ids, image_id });
                return res;
            } catch (e) {
                console.error("Error creating group: ", e);
                return { success: false, message: "Error creating group" };
            }
        }
        return { success: false, message: "Socket not connected" };
    }, []);

    const remove_group = injectCallback(async (topic_id: number) => {
        const socket = socketInstance.getState();
        if (socket) {
            try {
                const res = await socket.timeout(10000).emitWithAck('remove_group', { topic_id });
                return res;
            } catch (e) {
                console.error("Error removing group: ", e);
                return { success: false, message: "Error removing group" };
            }
        }
        return { success: false, message: "Socket not connected" };
    }, []);

    const group_add_user = injectCallback(async (topic_id: number, user_ids: number[]) => {
        const socket = socketInstance.getState();
        if (socket) {
            try {
                const res = await socket.timeout(10000).emitWithAck('group_add_user', { topic_id, user_ids });
                return res;
            } catch (e) {
                console.error("Error adding users to group: ", e);
                return { success: false, message: "Error adding users to group" };
            }
        }
        return { success: false, message: "Socket not connected" };
    }, []);

    const group_remove_user = injectCallback(async (topic_id: number, other_user_id: number) => {
        const socket = socketInstance.getState();
        console.log('remove user from group')
        if (socket) {
            try {
                const res = await socket.timeout(10000).emitWithAck('group_remove_user', { topic_id, other_user_id });
                const { success, topic } = res || {};
                console.log('remove user from gorup', res)
                if (success && topic) {
                    updateTopic(topic_id, topic);
                }
                return { success: true }
            } catch (e) {
                console.error("Error removing user from group: ", e);
                return { success: false, message: "Error removing user from group" };
            }
        }
        return { success: false, message: "Socket not connected" };
    }, []);

    const update_group = injectCallback(async (topic_id: number, { image_id }: { image_id: number | string | null }) => {
        const socket = socketInstance.getState();
        if (socket) {
            try {
                const res = await socket.timeout(30000).emitWithAck('update_group', { topic_id, image_id });
                const { success, topic } = res || {};
                console.log('update group', res)
                if (success && topic) {
                    updateTopic(topic_id, topic);
                }
                return { success: true }
            } catch (e) {
                console.error("Error updating group: ", e);
                return { success: false, message: "Error updating group" };
            }
        }
        return { success: false, message: "Socket not connected" };
    }, []);

    return api().addExports({
        sendMessage, resendMessage, fetchMessages, notifyServerForMessageSeen,
        editMessage, deleteMessage,
        create_group, remove_group, group_add_user, group_remove_user, update_group
    });
});

export const keepInstanceAliveAtom = atom("keep_message_receiver_alive", (context: IContext) => {
    const topicInstances = injectAtomValue(messageManagerAtom, [context, NO_FILTER]); // we just want to keep messageManager alive to keep all message atoms alive, we don't use the value of it here
    const activeTopicID = injectAtomValue(activeTopicAtom, [context]);
    const messageReceiverInstance = injectAtomInstance(messageReceiverAtom, [context]);
    const currentFilter = injectAtomValue(selectedFilterAtom, [context, activeTopicID ?? 0]);
    const messageReceiverInstanceForFilter = injectAtomValue(messageManagerAtom, [context, currentFilter]) // we need to keep the instance for every filter alive, otherwise when we switch filter the instance will be recreated and we will lose the socket listeners and the messages in the current topic will be lost until we fetch again
    const { ecosystem } = injectSelf();

    const why = injectWhy()
    if (Array.isArray(why) && activeTopicID) {
        const clearedFilter = why.find(w => typeof w === 'object' && w.sourceId === 'selected_filter' && w.newState?.type === FilterTypes.NO_FILTER)
        if (clearedFilter && clearedFilter.oldState && clearedFilter.oldState.type && clearedFilter.oldState.type !== FilterTypes.NO_FILTER) {
            const messageAtomFamilyForFiltered = ecosystem.getInstance(messagesAtomFamily, [activeTopicID, clearedFilter.oldState as IFilter, context])
            messageAtomFamilyForFiltered.exports.clearCache()
        }
    }

    // console.log('keepInstanceAliveAtom', { activeTopicID, currentFilter })

    injectEffect(() => {
        if (!activeTopicID) {
            return
        }

        const messageAtomFamilyInstance = ecosystem.getInstance(messagesAtomFamily, [activeTopicID, currentFilter, context]);
        if (!messageAtomFamilyInstance.exports.isFetchedBefore()) {
            ecosystem.getInstance(messageReceiverAtom, [context]).exports.fetchMessages(activeTopicID, { filter: currentFilter });
        }
    }, [activeTopicID, currentFilter]);


    injectEffect(() => {
        if (activeTopicID) {
            messageReceiverInstance.exports.notifyServerForMessageSeen(activeTopicID);
        }
    }, [activeTopicID]);
})